import BoltIcon from '@mui/icons-material/Bolt';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink, Navigate } from 'react-router-dom';

import { BrandMark } from '../components/BrandLogo';
import { LegalFooter } from '../components/legal/LegalFooter';
import { useAuthStore } from '../stores/auth-store';

const FEATURES = [
  { icon: <SpeedIcon />, title: 'SLA pilotés', text: 'Suivi en temps réel, alertes automatiques.' },
  {
    icon: <GroupsIcon />,
    title: 'Collaboration',
    text: 'Équipes, agents et clients sur une seule plateforme.',
  },
  { icon: <SecurityIcon />, title: 'RGPD', text: 'Sécurité, audit et conformité par défaut.' },
];

export function HomePage() {
  const status = useAuthStore((s) => s.status);

  if (status === 'authenticated') {
    return <Navigate to="/app" replace />;
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box
        component="header"
        sx={{
          py: 2,
          px: { xs: 2, md: 4 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(8px)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <BrandMark size={36} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Premium Energy
              </Typography>
            </Stack>
            <Button component={RouterLink} to="/login" variant="contained" size="small">
              Se connecter
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 6, md: 10 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 8 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1, maxWidth: 600 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 99,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: 12,
                fontWeight: 600,
                mb: 3,
                opacity: 0.9,
              }}
            >
              <BoltIcon sx={{ fontSize: 14 }} />
              Plateforme interne de support
            </Box>
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                lineHeight: 1.05,
                mb: 2,
              }}
            >
              Le support de Premium Energy,{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #f97316 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                organisé.
              </Box>
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: '1.1rem', mb: 4, maxWidth: 500 }}
            >
              Tickets, SLA, équipes, audit. Tout ce qu&apos;il faut pour gérer le support de A à Z,
              en interne.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="large"
                sx={{ px: 4 }}
              >
                Se connecter
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
              L&apos;accès se fait sur invitation par votre administrateur.
            </Typography>
          </Box>

          <Box sx={{ flex: 1, width: '100%', maxWidth: 500 }}>
            <Stack spacing={2}>
              {FEATURES.map((f) => (
                <Stack
                  key={f.title}
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 30px -10px rgba(15, 23, 42, 0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      flexShrink: 0,
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {f.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {f.text}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Container>
      <LegalFooter />
    </Box>
  );
}
